import { CategoryNaturalKey } from './models/category';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ColDef, GridOptions } from 'ag-grid-community';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, publishReplay, refCount, tap, switchMap, takeUntil } from 'rxjs/operators';
import { CategoriesStorage, Category, Report } from '.';
import { CovanceFilter, FilterFieldValue, SerializedFilter, FilterFieldType } from '../covance-filter';
import { DocumentsStorage, DOCUMENTS_STORAGE } from './documents-storage';
import { FilterItemsLoader, FILTER_ITEM_LOADER } from './filter-items-loader';
import { routeParams } from '../shared/route-helpers';
import { FilterResolver, FILTER_RESOLVER } from './filter-resolver';
import { isEmpty } from 'lodash';
import { UserStorage, User } from '../user';

class FilterFieldsAndCategory {
    constructor (public readonly fields: FilterFieldValue[], public readonly category: Category) {
    }
}

@Component({
    selector: 'filtered-grid',
    template: require('./filtered-grid.component.html'),
    styles: [require('./filtered-grid.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilteredGridComponent implements OnDestroy {

    @Input()
    public filterId: string;

    @Input()
    public filterProtocolId: number = null;

    @Input()
    public warningThreshhold = 0;

    @Input()
    public columns: ColDef[];

    @Input()
    public categories: Category[];

    @Input()
    public mandatoryFilter: string;

    @Input()
    public mandatoryFilterValue$: Observable<string> = of(null);

    @Input()
    public mandatoryFilterRequired = false;

    @Output()
    public categoryChanged = new EventEmitter<CategoryNaturalKey>();

    @Input()
    public hideFilterExpandButton = false;

    @Input()
    public showExport = this.userStorage.currentUser
        .pipe(
            map(user => {

                let showExport = false;

                if (user.isAdmin || user.isInternal) {
                    showExport = true;
                }

                return showExport;
            }
        ));

    @Input()
    public set floatingFilter(value: boolean) {
        this.gridOptions.floatingFilter = value;
    }
    
    @Input()
    public quickFilterText: string;

    public showLargeDataWarning: Observable<boolean>;
    public filterField$: Observable<FilterFieldValue[]>;
    public data: Observable<any>;
    public selectedCategory: Observable<Category>;
    public selectedCategoryId: Observable<CategoryNaturalKey>;
    public dataSwitch = new Subject<boolean>();
    public showFilters: Observable<boolean>;
    public filtersExportData: Observable<SerializedFilter[]>;
    public quickSearchSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    public masterFilterItems: Observable<{key: string, value: string}[]>;
    public selectedMasterFilterItem$: Observable<string>;
    public mandatoryFilterVisible: Observable<boolean>;

    public readonly gridOptions: GridOptions = {
        floatingFilter: true,        
        enableFilter: false,
        floatingFiltersHeight: 32,
        rowClassRules: {
            'hidden-document': ({ data }: { data: Report }) => data.isHidden
        }
    };

    public adminFilterGroup: Observable<string>;
    private subscription: Subscription;

    constructor(
        @Inject(FILTER_ITEM_LOADER) private filterItemsLoader: FilterItemsLoader,
        @Inject(DOCUMENTS_STORAGE) private documentLoader: DocumentsStorage,
        @Inject(FILTER_RESOLVER) private filtersResolver: FilterResolver,
        private route: ActivatedRoute,
        private categoriesStorage: CategoriesStorage,
        private filterService: CovanceFilter,
        private userStorage: UserStorage) {

        this.selectedCategoryId = this.route.params
            .pipe(
                map(x => x[routeParams.categoryId] as CategoryNaturalKey | undefined),
                filter(categoryNaturalKey => !!categoryNaturalKey),
                distinctUntilChanged(),
                publishReplay(1),
                refCount(),
            );

        this.selectedCategory = this.selectedCategoryId
            .pipe(
                switchMap(categoryId => this.categoriesStorage.findCategoryById(categoryId)),
                publishReplay(1),
                refCount(),
            );                
    }

    public ngOnInit() {
        const filterFieldAndCategory$ = this.selectedCategory
            .pipe(
                map(category => {
                    const fields = this.filtersResolver.resolveFilters(category.categoryId);
                    return new FilterFieldsAndCategory(fields, category);
                }),
                publishReplay(1),
                refCount(),
            );

        this.filterField$ = filterFieldAndCategory$.pipe(map(({fields}) => fields));
        this.showFilters = this.filterField$.pipe(map(f => !isEmpty(f)));
        this.mandatoryFilterVisible = this.filterField$.pipe(map(f => !!this.findMandatoryFilter(f)));

        const filterFlow$ = filterFieldAndCategory$
            .pipe(
                switchMap(({category, fields}) => this.filterService.listen(fields)
                    .pipe(
                        takeUntil(this.dataSwitch),
                        map(() => new FilterFieldsAndCategory(fields, category)))),
                publishReplay(1),
                refCount(),
            );

        this.data = filterFlow$
            .pipe(
                switchMap(({fields, category}) => {
                    const mandatoryFilter = this.findMandatoryFilter(fields);
                    const mandatoryFilterIsOk = !this.mandatoryFilterRequired || !mandatoryFilter || mandatoryFilter.value;
                    return merge(
                    of(null),
                    mandatoryFilterIsOk
                    ? this.documentLoader.loadDocumentsByCategory(category, fields)
                    : of([]));
                }),
                publishReplay(1),
                refCount()
            );

        this.showLargeDataWarning = this.data.pipe(
            map((data: any[]) => !isEmpty(data) && this.warningThreshhold > 0 && data.length === this.warningThreshhold)
        );

        const masterFilterSelectedItem$: Observable<string> = filterFlow$
            .pipe(
                map(({fields}) => {
                    const mandatoryFilterItem = this.findMandatoryFilter(fields);
                    return mandatoryFilterItem ? mandatoryFilterItem.value : null;
                }),
                distinctUntilChanged(),
            );

        this.subscription = filterFieldAndCategory$
            .pipe(
                switchMap(({fields, category}) => {
                    const hasMandatoryFilter = this.findMandatoryFilter(fields);
                    const reloadFilterItemsSwitch$ = hasMandatoryFilter ?
                        masterFilterSelectedItem$
                            .pipe(
                                tap(() => this.filterService.setFiltersItems([], fields)),
                                filter(c => !!c),
                                takeUntil(this.dataSwitch))
                        : of(null);
                    return this.filterItemsLoader.loadFilterItemsByCategory(
                        category,
                        reloadFilterItemsSwitch$)
                            .pipe(tap(i => this.filterService.setFiltersItems(i, fields)));
                    }
                ),
            ).subscribe();
        
        this.selectedMasterFilterItem$ = masterFilterSelectedItem$;

        this.filtersExportData = combineLatest([filterFlow$, this.quickSearchSubject$, this.mandatoryFilterValue$])
            .pipe(
                map(([fieldValues, quickSearchValue, mandatoryFilterValue]) => {
                    const filtersExportData = this.filterService.serializeFieldValues(fieldValues.fields);
                    filtersExportData.unshift({ title: 'Search', value: quickSearchValue });
                    if (mandatoryFilterValue) {
                        const mandatoryFilterData = filtersExportData.find(fd => fd.title === this.findMandatoryFilter(fieldValues.fields).def.title);
                        mandatoryFilterData.value = mandatoryFilterValue;
                    }
                    return filtersExportData;
                }),
            );

        this.adminFilterGroup = this.selectedCategory
            .pipe(
                map(c => `${this.filterId}-${c.categoryId}`),
            );
    }

    public ngOnDestroy() {  
        this.subscription.unsubscribe();    
    }

    public goToCategory(categoryId: CategoryNaturalKey) {
        this.dataSwitch.next(false);
        this.categoryChanged.emit(categoryId);
    }

    public onQuickSearchChanged(value: string) {
        this.quickSearchSubject$.next(value);
    }

    private findMandatoryFilter(filterDefs: FilterFieldValue[]) {
        return filterDefs.find( fd => fd.def.type === FilterFieldType.External && fd.def.queryParam === this.mandatoryFilter) || null;
    }
}
